import { useState } from "react";
import { useNavigate } from "react-router";
import styled from "@emotion/styled";
import { api } from "@/modules/api";
import { fileModule } from "@/modules/file";
import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";

const CreatePageContainer = styled.div`
  padding: 20px;
  max-width: 600px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-weight: 500;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  min-height: 200px;
  font-family: monospace;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  margin-top: 10px;
`;

const SuccessMessage = styled.div`
  color: #2e7d32;
  margin-top: 10px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 10px 20px;
  background: none;
  border: none;
  border-bottom: 2px solid ${props => (props.active ? "#1976d2" : "transparent")};
  color: ${props => (props.active ? "#1976d2" : "#666")};
  font-weight: ${props => (props.active ? "600" : "400")};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: -2px;

  &:hover {
    color: #1976d2;
  }
`;

const FileUploadContainer = styled.div`
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  background-color: #fafafa;
  transition: all 0.2s ease;

  &:hover {
    border-color: #1976d2;
    background-color: #f5f5f5;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileUploadLabel = styled.label`
  display: inline-block;
  padding: 10px 20px;
  background-color: #1976d2;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #1565c0;
  }
`;

const FileInfo = styled.div`
  margin-top: 20px;
  padding: 15px;
  background-color: #e3f2fd;
  border-radius: 4px;
  text-align: left;
`;

const HelpText = styled.p`
  color: #666;
  font-size: 14px;
  margin: 10px 0;
`;

export const CapturedWebsitesCreatePage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const [uploadMethod, setUploadMethod] = useState<"paste" | "file">("paste");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [websiteUrl, setWebsiteUrl] = useState("");
  const [websiteTitle, setWebsiteTitle] = useState("");
  const [htmlContent, setHtmlContent] = useState("");

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type === "text/html" || file.name.endsWith(".html") || file.name.endsWith(".htm")) {
        setSelectedFile(file);
        setError(null);
      } else {
        setError("Please select an HTML file (.html or .htm)");
        setSelectedFile(null);
      }
    }
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      setError("Please select a file");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { fileId } = await fileModule.uploadFile({ file: selectedFile });
      setUploadedFileId(fileId);
      setIsLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsLoading(false);
    }
  };

  const handleUploadHtml = async () => {
    if (!htmlContent) {
      setError("Please enter HTML content");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create a blob from the HTML content
      const blob = new Blob([htmlContent], { type: "text/html" });
      const file = new File([blob], "website.html", { type: "text/html" });

      const { fileId } = await fileModule.uploadFile({ file });
      setUploadedFileId(fileId);
      setIsLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!uploadedFileId) {
      setError("Please upload HTML content first");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create the captured website
      const response = await api.post("/v2/captured-websites", {
        body: {
          website_url: websiteUrl,
          website_title: websiteTitle || undefined,
          uploaded_file_id: uploadedFileId,
          captured_at: new Date().toISOString(),
        },
      });

      if (response.error || !response.data) {
        throw new Error("Failed to create captured website");
      }

      // Navigate to the view page
      navigate(
        atlasRoutes.capturedWebsitesView({ params: { capturedWebsiteId: response.data.id } }).path
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsLoading(false);
    }
  };

  const resetUpload = () => {
    setUploadedFileId(null);
    setSelectedFile(null);
    setHtmlContent("");
    setError(null);
  };

  return (
    <CreatePageContainer>
      <PageTitle>Create Captured Website</PageTitle>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="url">Website URL*</Label>
          <Input
            id="url"
            type="url"
            value={websiteUrl}
            onChange={e => setWebsiteUrl(e.target.value)}
            placeholder="https://example.com"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="title">Website Title</Label>
          <Input
            id="title"
            type="text"
            value={websiteTitle}
            onChange={e => setWebsiteTitle(e.target.value)}
            placeholder="Example Website"
          />
        </FormGroup>

        <FormGroup>
          <Label>HTML Content*</Label>
          <TabContainer>
            <Tab
              type="button"
              active={uploadMethod === "paste"}
              onClick={() => {
                setUploadMethod("paste");
                resetUpload();
              }}
            >
              Paste HTML
            </Tab>
            <Tab
              type="button"
              active={uploadMethod === "file"}
              onClick={() => {
                setUploadMethod("file");
                resetUpload();
              }}
            >
              Upload File
            </Tab>
          </TabContainer>

          {uploadMethod === "paste" ? (
            <>
              <TextArea
                id="html"
                value={htmlContent}
                onChange={e => setHtmlContent(e.target.value)}
                placeholder="<html>...</html>"
                required={!uploadedFileId}
                disabled={!!uploadedFileId}
              />
              {!uploadedFileId && (
                <Button
                  type="button"
                  onClick={handleUploadHtml}
                  disabled={isLoading || !htmlContent}
                >
                  {isLoading ? "Uploading HTML..." : "Upload HTML Content"}
                </Button>
              )}
            </>
          ) : (
            <FileUploadContainer>
              {!selectedFile ? (
                <>
                  <FileInput
                    id="file-upload"
                    type="file"
                    accept=".html,.htm,text/html"
                    onChange={handleFileSelect}
                    disabled={!!uploadedFileId}
                  />
                  <FileUploadLabel htmlFor="file-upload">Choose HTML File</FileUploadLabel>
                  <HelpText>Select an HTML file from your computer (.html or .htm)</HelpText>
                </>
              ) : (
                <FileInfo>
                  <strong>Selected file:</strong> {selectedFile.name}
                  <br />
                  <strong>Size:</strong> {(selectedFile.size / 1024).toFixed(2)} KB
                  <br />
                  <strong>Type:</strong> {selectedFile.type || "HTML"}
                  {!uploadedFileId && (
                    <>
                      <br />
                      <br />
                      <Button type="button" onClick={handleUploadFile} disabled={isLoading}>
                        {isLoading ? "Uploading File..." : "Upload File"}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        style={{ marginLeft: "10px" }}
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </FileInfo>
              )}
            </FileUploadContainer>
          )}
        </FormGroup>

        {uploadedFileId && (
          <SuccessMessage>
            HTML content uploaded successfully! File ID: {uploadedFileId}
            <Button
              type="button"
              onClick={resetUpload}
              style={{ marginLeft: "10px", padding: "4px 8px", fontSize: "12px" }}
            >
              Upload Different Content
            </Button>
          </SuccessMessage>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonGroup>
          <Button type="submit" disabled={isLoading || !uploadedFileId}>
            Create Captured Website
          </Button>
          <Button type="button" onClick={() => navigate(atlasRoutes.capturedWebsites({}).path)}>
            Cancel
          </Button>
        </ButtonGroup>
      </Form>
    </CreatePageContainer>
  );
};
